// Height rules shared by the bottom drawers.
//
// Why not plain `vh`: on phones, `100vh` is the page height with the browser's
// toolbars *tucked away*. While the toolbar is showing (Chrome's address bar,
// Safari's bottom bar) the real visible area is shorter, so a drawer sized in
// `vh` pokes out past the top of the screen — and its header, with the close
// button, goes with it. `dvh` tracks the visible area as the toolbars come and
// go, so the drawer re-fits on its own, with no JS needed to detect them.
//
// On top of that the drawer is never allowed to fill the screen on a phone:
// it always leaves TOP_GAP of the page (and the dimmed overlay) showing, so
// there is something to tap to dismiss it even if a toolbar misbehaves.
//
// Browsers that don't know `dvh` (pre-2022) keep the plain `vh` value.

const TOP_GAP = "72px";
const DESKTOP = "@media (min-width: 48em)"; // Chakra's `md`

// Fill-mode drawers leave only this much visible above them — enough to show
// the sheet is a sheet and give a sliver of overlay to tap, nothing more.
const FILL_GAP = "24px";

/**
 * Props to spread onto a bottom `DrawerContent`.
 *
 * @param {object} sizes
 * @param {number} sizes.mobile   Max height on phones, in vh/dvh units.
 * @param {number} sizes.desktop  Max height from the `md` breakpoint up.
 * @param {boolean} [sizes.fixed] Also pin `height` (not just cap it), for
 *   drawers whose body must fill the space, like the map.
 * @param {boolean} [sizes.fill]  Phones only: take everything the browser
 *   leaves visible minus FILL_GAP, however much that is with the toolbar
 *   showing — for immersive bodies (the map) where taller is always better.
 *   `mobile` is still used by browsers without dvh.
 */
export const bottomDrawerSize = ({
  mobile,
  desktop,
  fixed = false,
  fill = false,
}) => {
  const forProps = (value) =>
    fixed ? { height: value, maxHeight: value } : { maxHeight: value };

  const mobileDvh = fill
    ? `calc(100dvh - ${FILL_GAP})`
    : `min(${mobile}dvh, calc(100dvh - ${TOP_GAP}))`;

  return {
    sx: {
      ...forProps(`${mobile}vh`),
      [DESKTOP]: forProps(`${desktop}vh`),
      "@supports (height: 100dvh)": {
        ...forProps(mobileDvh),
        [DESKTOP]: forProps(`${desktop}dvh`),
      },
    },
  };
};
